import { useCallback, useEffect, useRef, useState } from 'react';
import { IoChevronDown } from 'react-icons/io5';
import hamburgerIcon from 'assets/image/icon-hamburger.svg';
import clsx from 'clsx';
import { IAnyType } from 'common/types/common';
import { getLanguageSwitchLink, LanguageSwitchData } from 'common/utils/languageSwitch';
import gsap from 'gsap/dist/gsap';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';

import { Button } from '../Button';
import { MenuModal } from '../MenuModal';

const logoDark =
  'https://firebasestorage.googleapis.com/v0/b/nordit-83a0b.appspot.com/o/logo%2FNordit%20logo%20-%20primary%20black%2FNordit%20logo%20-%20primary%20black.svg?alt=media&token=3dc59342-ef7f-4fb2-bd6a-905c15126e99';
const logoWhite =
  'https://firebasestorage.googleapis.com/v0/b/nordit-83a0b.appspot.com/o/logo%2FNordit%20logo%20-%20primary%20white%2FNordit%20logo%20-%20primary%20white.svg?alt=media&token=53c70a77-ccd9-4fc6-8019-47aba940c62f';

gsap.registerPlugin(ScrollTrigger);

interface Props {
  alternate?: { hrefLang?: string; slug?: string };
  dark?: boolean;
  languageSwitch?: LanguageSwitchData;
}

export const Header = ({ alternate, dark, languageSwitch }: Props) => {
  const [isTop, setIsTop] = useState(true);
  const [isServicesMenuOpen, setIsServicesMenuOpen] = useState(false);
  const [servicesMenuTop, setServicesMenuTop] = useState(88);

  const { i18n, t } = useTranslation('common');
  const modalRef = useRef<IAnyType>(null);
  const el = useRef(null);
  const headerRef = useRef<HTMLElement>(null);

  const showMenu = () => {
    const visible = modalRef.current?.toggleMenu();
    const q = gsap.utils.selector(el);

    if (visible) {
      gsap.timeline({ delay: 0 }).to(q('#header-menu'), { duration: 0.5, opacity: 0 });
      gsap.timeline({ delay: 0 }).to(q('#header-logo'), { duration: 0.4, opacity: 0 });
      setTimeout(() => {
        document.getElementById('menu-trigger')?.classList.add('ring-2');
        if (!dark) {
          document.getElementById('menu-trigger')?.classList.remove('invert');
        }
        if (dark) {
          document.getElementById('header')?.classList.remove('bg-gradient');
          document.getElementById('header')?.classList.add('bg-transparent');
        } else {
          document.getElementById('header')?.classList.remove('bg-white');
          document.getElementById('header')?.classList.add('bg-transparent');
        }
      }, 0);
    } else {
      gsap.timeline({ delay: 0.8 }).to(q('#header-logo'), { duration: 0.5, opacity: 1 });
      gsap.timeline({ delay: 1 }).to(q('#header-menu'), { duration: 0.5, opacity: 1 });
      setTimeout(() => {
        if (!dark) {
          document.getElementById('menu-trigger')?.classList.add('invert');
        }
      }, 300);
      setTimeout(() => {
        document.getElementById('menu-trigger')?.classList.remove('ring-2');
        if (dark) {
          document.getElementById('header')?.classList.remove('bg-transparent');
          document.getElementById('header')?.classList.add('bg-gradient');
        } else {
          document.getElementById('header')?.classList.remove('bg-transparent');
          document.getElementById('header')?.classList.add('bg-white');
        }
      }, 1000);
    }
  };

  const addListeners = useCallback(() => {
    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
      const adjustMenuIconPosition = () => {
        if (document) {
          const menuTrigger = document.getElementById('menu-trigger');
          if (menuTrigger) {
            menuTrigger.style.left = '';
            menuTrigger.style.left = `${menuTrigger?.offsetLeft}px`;
          }
        }

        if (headerRef.current) {
          const currentHeight = headerRef.current.getBoundingClientRect().bottom;
          setServicesMenuTop(currentHeight);
        }
      };

      window.addEventListener('resize', adjustMenuIconPosition);
      window.addEventListener('load', adjustMenuIconPosition);
      adjustMenuIconPosition();

      return () => {
        window.removeEventListener('resize', adjustMenuIconPosition);
        window.removeEventListener('load', adjustMenuIconPosition);
      };
    }

    return undefined;
  }, []);

  useEffect(() => {
    const removeListeners = addListeners();
    const showAnim = gsap
      .from('.main-tool-bar', {
        yPercent: -100,
        paused: true,
        duration: 0.2,
      })
      .progress(1);

    const scrollTrigger = ScrollTrigger.create({
      start: 'top top',
      end: 99999,
      onUpdate: self => {
        if (self.direction === 1 && self.scroll() > 100) {
          showAnim.reverse();
        } else if (self.direction === -1) {
          showAnim.play();
        }
      },
    });

    // Scroll listener for bg change
    const handleScroll = () => {
      setIsTop(window.scrollY < 100);
      if (headerRef.current) {
        setServicesMenuTop(headerRef.current.getBoundingClientRect().bottom);
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => {
      removeListeners?.();
      scrollTrigger.kill();
      showAnim.kill();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [addListeners]);

  const base = t('company.urlAlternate');
  const menu: IAnyType = t('menu.items', { returnObjects: true });
  const servicesSlug = t('servicesSlug');
  const servicesMenuItemsRaw = t('menu.servicesDropdown.items', { returnObjects: true });
  const servicesMenuItems = Array.isArray(servicesMenuItemsRaw) ? servicesMenuItemsRaw : [];
  const languageSwitchLink = getLanguageSwitchLink({
    currentLocale: i18n.language,
    alternate,
    baseAlternate: base,
    localizations: languageSwitch?.localizations,
    query: languageSwitch?.query,
    routePrefix: languageSwitch?.routePrefix,
  });
  const otherLanguage = languageSwitchLink?.label;
  const otherLanguageSlug = languageSwitchLink?.href;

  const closeServicesMenu = () => {
    setIsServicesMenuOpen(false);
  };

  const openServicesMenu = () => {
    setIsServicesMenuOpen(true);
  };

  return (
    <div ref={el} className="z-[99999]">
      <header
        ref={headerRef}
        id="header"
        className={clsx('main-tool-bar fixed -top-40 z-[99999] w-full', {
          'bg-gradient text-white': dark,
          'bg-white text-[#16324f]': !dark && !isTop,
          'bg-transparent': !dark && isTop,
        })}>
        <div className="container relative flex items-center justify-between px-6 py-8 lg:py-5">
          <Link href="/">
            <Image
              id="header-logo"
              className="relative w-[130px]"
              src={dark ? logoWhite : logoDark}
              alt="Nordit software development"
              width={412}
              height={70}
              unoptimized
            />
          </Link>

          <button className="menu-trigger lg:hidden" onClick={showMenu}>
            <Image src={hamburgerIcon} alt="Menu" width={30} height={30} unoptimized />
          </button>

          <nav className="flex flex-row">
            <ul
              id="header-menu"
              className={clsx('menu-content hidden items-center justify-center lg:flex', {
                'mr-4': otherLanguageSlug,
                'mr-6': !otherLanguageSlug,
              })}>
              {Array.isArray(menu) &&
                menu.length &&
                menu?.map((item: IAnyType) => {
                  const hasServicesDropdown =
                    item.link === servicesSlug && servicesMenuItems?.length;

                  if (!hasServicesDropdown) {
                    return (
                      <li key={item.link} onMouseEnter={closeServicesMenu}>
                        <Link href={item.link}>{item.title}</Link>
                      </li>
                    );
                  }

                  return (
                    <li
                      key={item.link}
                      className="static"
                      onMouseEnter={openServicesMenu}
                      onFocus={openServicesMenu}
                      onBlur={event => {
                        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                          closeServicesMenu();
                        }
                      }}>
                      <Link href={item.link}>
                        <div
                          className={clsx('flex flex-row items-center', {
                            'text-primary': isServicesMenuOpen,
                          })}>
                          <span>{item.title}</span>
                          <IoChevronDown
                            className={clsx('mt-1 ml-[2px]', {
                              'text-primary': isServicesMenuOpen,
                            })}
                            size={20}
                          />
                        </div>
                      </Link>
                    </li>
                  );
                })}
              <li className="ml-8">
                <Button
                  className="!min-w-unset text-sm !pt-2 !lg:pt-2 !pb-3 !lg:pb-3 !px-6 !lg:px-8"
                  href={t('contactProjectSlug')}
                  showArrow={true}
                  type="primary">
                  {t('workWithUs')}
                </Button>
              </li>
              {otherLanguageSlug && (
                <li key={otherLanguage} className="ml-8 mt-0.5 scale-90">
                  <a href={otherLanguageSlug}>{otherLanguage}</a>
                </li>
              )}
            </ul>
            <button className="relative mt-1.5 hidden lg:block" onClick={showMenu}>
              <Image
                id="menu-trigger"
                className={clsx(
                  'rounded-full ring-white/20 ring-offset-8 ring-offset-white/10 transition-all duration-500 focus:outline-none',
                  { invert: !dark },
                )}
                src={hamburgerIcon}
                alt="Menu"
                width={30}
                height={30}
                unoptimized
              />
            </button>
          </nav>
        </div>
      </header>
      <div
        className={clsx(
          'pointer-events-none fixed left-0 right-0 transition-all duration-300 lg:block z-[10000] top-[88px]',
          {
            'pointer-events-auto translate-y-0 opacity-100': isServicesMenuOpen,
            'hidden -translate-y-2 opacity-0': !isServicesMenuOpen,
          },
        )}
        onMouseEnter={openServicesMenu}
        onMouseLeave={closeServicesMenu}>
        <div className="container px-6">
          <div
            className={clsx(
              'rounded-[30px] border backdrop-blur-lg px-5 py-5 md:px-8 md:py-8 lg:px-12 lg:py-8',
              {
                'bg-[#21172d]/90 border-white/10': dark,
                'bg-white/90 border-black/10': !dark,
              },
            )}>
            <div
              className={clsx('text-lg font-bold max-w-[45ch] mb-4', {
                'text-white': dark,
                'text-black': !dark,
              })}>
              {t('menu.servicesDropdown.title')}
            </div>
            <div className="grid gap-2 md:grid-cols-4">
              {servicesMenuItems.map((service: IAnyType) => (
                <Link key={service.slug} href={service.slug} onClick={closeServicesMenu}>
                  <div
                    className={clsx(
                      'border p-4 lg:p-6 h-full transition-colors transition-border duration-500 rounded-[20px]',
                      {
                        'bg-[#21172d] border-white/10 hover:bg-[#140f1d]': dark,
                        'bg-white border-black/5 hover:shadow hover:border-black/15': !dark,
                      },
                    )}>
                    <div className="flex h-full justify-between flex-col">
                      <div className="flex items-start gap-2 mb-2">
                        <span className="h-px w-5 bg-primary mt-2.5" />
                        <div className="font-semibold uppercase text-primary text-sm">
                          {service.title}
                        </div>
                      </div>
                      <div
                        className={clsx('text-sm xl:text-md', {
                          'text-white/90': dark,
                          'text-black/90': !dark,
                        })}>
                        {service.description}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div
        aria-hidden={!isServicesMenuOpen}
        className={clsx(
          'pointer-events-none fixed inset-x-0 bottom-0 transition-opacity duration-300 lg:block z-[9998]',
          {
            'pointer-events-auto opacity-100': isServicesMenuOpen,
            'hidden -translate-y-2 opacity-0': !isServicesMenuOpen,
            'bg-[#140f1d]/40 backdrop-blur-md': dark,
            'bg-white/90 backdrop-blur-sm': !dark,
          },
        )}
        style={{ top: `${servicesMenuTop}px` }}
        onMouseEnter={closeServicesMenu}
      />

      <MenuModal
        ref={modalRef}
        otherLanguage={otherLanguage}
        otherLanguageSlug={otherLanguageSlug}
      />
    </div>
  );
};
